'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
// import { IconDotsVertical } from '@tabler/icons-react'
import { questionService } from '@/services/questionsService'
import {
  CaseStudyListResponse,
  CaseStudyResponse,
  CaseStudyQuestionListResponse,
  CaseStudyQuestion,
  QuestionSection,
} from '@/types/question'
import { cn, truncate } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
// import { useModulesStore } from '@/stores/useModulesStore'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from '@/components/ui/dropdown-menu'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import QuestionDrawer from '@/features/question-lib/components/QuestionDrawer'
import { TypeValue } from '@/features/question-lib/constant'

interface CaseStudyTableProps {
  moduleId?: string
  section?: QuestionSection | string
  caseStudies?: CaseStudyResponse[]
  questionsByCaseStudy?: Record<number, CaseStudyQuestion[]>
  onSelectionChange?: (payload: {
    caseStudyId: number
    selectedIds: string[]
    totalTime: number
    totalMarks: number
    selectedCount: number
    selectedItems: CaseStudyQuestion[]
  }) => void
  reviewModeSelected?: Record<number, string[]>
  initialSelected?: Record<number, string[]>
  onFetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}

export default function CaseStudyTable({
  moduleId,
  section,
  caseStudies: caseStudiesProp,
  questionsByCaseStudy,
  onSelectionChange,
  reviewModeSelected,
  initialSelected,
  onFetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: CaseStudyTableProps) {
  const fetchLockRef = React.useRef(false)
  const { data } = useQuery({
    queryKey: ['case-studies', moduleId, section],
    queryFn: () =>
      questionService.getCaseStudies({
        module_id: Number(moduleId),
        section: section as QuestionSection,
        search: '',
        page: 1,
        size: 10,
      }),
    enabled: !caseStudiesProp && !!moduleId && !!section,
  })

  const caseStudies: CaseStudyResponse[] = useMemo(() => {
    const fallback = (() => {
      const payload = (data as unknown as CaseStudyListResponse | undefined)
        ?.data
      if (!payload) return []
      return Array.isArray(payload) ? payload : [payload]
    })()
    const list = caseStudiesProp ?? fallback
    if (!reviewModeSelected) return list
    return list.filter(
      (cs) => (reviewModeSelected?.[cs.case_study_id] || []).length > 0
    )
  }, [data, caseStudiesProp, reviewModeSelected])

  // if (caseStudies.length === 0)
  //   return (
  //     <Stack gap={'8px'}>
  //       <Text className='text-[16px] font-extralight text-[#1D2939]'>
  //         Case Study Questions
  //       </Text>
  //       <p className='text-center'>No Case Study available.</p>
  //     </Stack>
  //   )

  return (
    <Stack gap={'8px'}>
      <Text className='text-[16px] font-extralight text-[#1D2939]'>
        Case Study Questions
      </Text>
      {caseStudies.length === 0 ? (
        <div className='max-h-[420px] overflow-auto rounded-md border bg-white'>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={1} className='h-24 text-center'>
                  No results.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
      <Accordion
        type='multiple'
        className='max-h-[420px] overflow-auto rounded-md border bg-white'
        onScroll={(e) => {
          const el = e.currentTarget
          if (!hasNextPage || !onFetchNextPage) return
          if (fetchLockRef.current) return
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            fetchLockRef.current = true
            onFetchNextPage()
            setTimeout(() => (fetchLockRef.current = false), 500)
          }
        }}
      >
        {caseStudies.map((cs) => (
          <CaseStudyItem
            key={cs.case_study_id}
            item={cs}
            onSelectionChange={onSelectionChange}
            selectedPreviewIds={reviewModeSelected?.[cs.case_study_id]}
            initialSelectedIds={initialSelected?.[cs.case_study_id]}
            questionsOverride={questionsByCaseStudy?.[cs.case_study_id]}
          />
        ))}
        {isFetchingNextPage && (
          <Text className='block py-2 text-center text-[12px] text-[#475467]'>
            Loading more...
          </Text>
        )}
      </Accordion>
      )}
    </Stack>
  )
}

interface CaseStudyItemProps {
  item: CaseStudyResponse
  onSelectionChange?: (payload: {
    caseStudyId: number
    selectedIds: string[]
    totalTime: number
    totalMarks: number
    selectedCount: number
    selectedItems: CaseStudyQuestion[]
  }) => void
  selectedPreviewIds?: string[]
  questionsOverride?: CaseStudyQuestion[]
  initialSelectedIds?: string[]
}

const CaseStudyItem = React.memo(function CaseStudyItem({
  item,
  onSelectionChange,
  selectedPreviewIds,
  questionsOverride,
  initialSelectedIds,
}: CaseStudyItemProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([])
  const isPreview = Array.isArray(selectedPreviewIds)

  const { data, isFetching } = useQuery({
    queryKey: ['case-study-questions', item.case_study_id, isOpen],
    queryFn: () => questionService.getQuestionByCaseStudy(item.case_study_id),
    enabled: isOpen && !questionsOverride,
  })

  const questions = useMemo(() => {
    if (questionsOverride && Array.isArray(questionsOverride))
      return questionsOverride
    const payload = data as unknown as CaseStudyQuestionListResponse | undefined
    if (!payload) return []
    const list = payload.questions ?? payload.question ?? []
    return list
  }, [data, questionsOverride])

  const isAllSelected =
    questions.length > 0 && selectedIds.length === questions.length
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < questions.length

  function handleToggleAll(value: boolean | 'indeterminate') {
    if (isPreview) return
    if (!value) {
      setSelectedIds([])
      onSelectionChange?.({
        caseStudyId: item.case_study_id,
        selectedIds: [],
        totalTime: 0,
        totalMarks: 0,
        selectedCount: 0,
        selectedItems: [],
      })
      return
    }
    setSelectedIds(questions.map((q) => q.question_id))
  }

  function handleToggleRow(questionId: string | number, checked: boolean) {
    if (isPreview) return
    if (checked) {
      setSelectedIds((prev) => {
        const s = String(questionId)
        if (prev.map(String).includes(s)) return prev
        return [...prev, questionId]
      })
    } else {
      setSelectedIds((prev) =>
        prev.filter((id) => String(id) !== String(questionId))
      )
    }
  }

  useEffect(() => {
    if (!isPreview || !isOpen) return
    const previewIds = (selectedPreviewIds || []).map(String)
    setSelectedIds(previewIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, isOpen, JSON.stringify(selectedPreviewIds)])

  // Set initial selection on first open (non-preview)
  useEffect(() => {
    if (isPreview || !isOpen) return
    if (!initialSelectedIds || initialSelectedIds.length === 0) return
    setSelectedIds(initialSelectedIds.map(String))
    // run once per open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isPreview) return
    if (!questionsOverride) return
    if (!initialSelectedIds || initialSelectedIds.length === 0) return
    const next = initialSelectedIds.map(String)
    const current = selectedIds.map(String)
    const isSame =
      next.length === current.length && next.every((id) => current.includes(id))
    if (!isSame) setSelectedIds(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsOverride, JSON.stringify(initialSelectedIds)])

  useEffect(() => {
    if (!onSelectionChange) return
    if (!questions || questions.length === 0) {
      // onSelectionChange({
      //   caseStudyId: item.case_study_id,
      //   selectedIds: [],
      //   totalTime: 0,
      //   totalMarks: 0,
      //   selectedCount: 0,
      //   selectedItems: [],
      // })
      return
    }
    const selectedIdStrings = selectedIds.map(String)
    let totalTime = 0
    let totalMarks = 0
    const selectedItems: CaseStudyQuestion[] = []
    for (const q of questions) {
      if (selectedIdStrings.includes(String(q.question_id))) {
        totalTime += q.time || 0
        totalMarks += q.marks || 0
        selectedItems.push(q)
      }
    }
    onSelectionChange({
      caseStudyId: item.case_study_id,
      selectedIds: selectedIdStrings,
      totalTime,
      totalMarks,
      selectedCount: selectedItems.length,
      selectedItems,
    })
  }, [selectedIds, questions, onSelectionChange, item.case_study_id])

  const displayedQuestions = isPreview
    ? questions.filter((q) =>
        selectedIds.map(String).includes(String(q.question_id))
      )
    : questions

  const [quesntionId, setQuesionId] = useState<number>(0)
  // const { setSelectedQuestion } = useModulesStore()
  const COL = {
    select:
      'w-[52px] min-w-[52px] max-w-[52px] text-center text-[14px] font-medium text-[#002652]',
    id: 'text-[14px] font-medium text-[#002652] w-[230px] min-w-[230px] max-w-[230px]',
    chapter: 'text-[14px] font-medium text-[#002652] w-[220px] min-w-[220px] max-w-[220px]',
    question: 'text-[14px] font-medium text-[#002652] !w-[220px] !min-w-[220px] !max-w-[220px]',
    sub: 'text-[14px] font-medium text-[#002652] w-[10%] min-w-[10%] max-w-[10%]',
    time: 'text-[14px] font-medium text-[#002652] w-[10%] min-w-[10%] max-w-[10%]',
    marks: 'text-[14px] font-medium text-[#002652] w-[10%] min-w-[10%] max-w-[10%]',
    ai: 'text-[14px] font-medium text-[#002652] w-[10%] min-w-[10%] max-w-[10%]',
    tutor: 'text-[14px] font-medium text-[#002652] w-[10%] min-w-[10%] max-w-[10%]',
    action: 'text-[14px] font-medium text-[#002652] w-[60px] min-w-[60px] max-w-[60px]',
  }
  return (
    <>
      <AccordionItem value={`cs-${item.case_study_id}`}>
        <div className='px-4'>
          <div className='flex w-full items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Checkbox
                aria-label='Select all questions in this case study'
                checked={isAllSelected || (isIndeterminate && 'indeterminate')}
                disabled={isPreview}
                onCheckedChange={handleToggleAll}
                onClick={(e) => e.stopPropagation()}
              />
              <AccordionTrigger
                onClick={() => setIsOpen((v) => !v)}
                className='flex flex-1 items-center justify-between py-4 font-medium transition-all'
              >
                <Hstack justify={'between'} className='w-full'>
                  <Hstack gap={'8px'}>
                    <Text className='text-[14px] font-medium text-[#002652]'>
                      Case Study {item.case_study_id}
                    </Text>
                    <Text className='text-[14px] text-[#475467]'>
                      (
                      {isPreview
                        ? `${selectedIds.length} selected`
                        : selectedIds.length > 0
                          ? `${selectedIds.length} of ${questions.length || item.total_questions} selected`
                          : `${item.total_questions} questions`}
                      )
                    </Text>
                  </Hstack>
                </Hstack>
              </AccordionTrigger>
            </div>
            <Hstack gap={'24px'}>
              <Text className='text-[14px] text-[#1D2939]'>
                Duration:{' '}
                <span className='font-medium'>
                  {item.total_duration} minutes
                </span>
              </Text>
              <Text className='text-[14px] text-[#1D2939]'>
                Total Marks Available:{' '}
                <span className='font-medium'>{item.total_mark}</span>
              </Text>
            </Hstack>
          </div>
        </div>
        <AccordionContent>
          <div className='max-h-[360px] overflow-auto px-4'>
            <Table className='border-b'>
              <TableHeader className='text-bold'>
                <TableRow>
                  <TableHead className={COL.select}>Select</TableHead>
                  <TableHead className={`truncate ${COL.id}`}>
                    Question Info
                  </TableHead>
                  <TableHead className={COL.chapter}>Chapter</TableHead>
                  <TableHead className={COL.question}>Question Text</TableHead>
                  <TableHead className={`${COL.sub} text-center`}>
                    Sub-question
                  </TableHead>
                  <TableHead className={`${COL.time} text-center`}>
                    Time
                  </TableHead>
                  <TableHead className={`${COL.marks} text-center`}>
                    Marks Available
                  </TableHead>
                  <TableHead className={`${COL.ai} text-center`}>
                    Average AI Mark
                  </TableHead>
                  <TableHead className={`${COL.tutor} text-center`}>
                    Average Tutor Mark
                  </TableHead>
                  <TableHead className={COL.action}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={10} className='h-24 text-center'>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : displayedQuestions.length ? (
                  displayedQuestions.map((q) => (
                    <TableRow key={`q-${q.question_id}`}>
                      <TableCell className='text-center'>
                        <Checkbox
                          aria-label='Select this question'
                          checked={selectedIds
                            .map(String)
                            .includes(String(q.question_id))}
                          disabled={isPreview}
                          onCheckedChange={(v) =>
                            handleToggleRow(q.question_id, v === true)
                          }
                        />
                      </TableCell>
                      <TableCell className='font-medium'>
                        {q?.question_info}
                      </TableCell>
                      <TableCell>
                        {truncate(
                          Array.isArray(q?.chapter_name)
                            ? q.chapter_name.join(', ')
                            : String(q?.chapter_name || ''),
                          30
                        )}
                      </TableCell>
                      <TableCell className={`${COL.question} text-black! truncate`}>{truncate(q?.text, 50)}</TableCell>
                      <TableCell className='text-center'>
                        {q?.sub_questions}
                      </TableCell>
                      <TableCell className='text-center'>{q?.time}</TableCell>
                      <TableCell className='text-center'>{q?.marks}</TableCell>
                      <TableCell
                        className={cn(
                          'text-center px-0',
                          getMarksStyle(q?.avg_ai_marks)
                        )}
                      >
                        {getMarksPercentage(q?.avg_ai_marks)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-center px-0',
                          getMarksStyle(q?.avg_tutor_marks)
                        )}
                      >
                        {getMarksPercentage(q?.avg_tutor_marks)}
                      </TableCell>
                      <TableCell>
                        {/* <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='ghost' className='h-8 w-8 p-0'>
                              <span className='sr-only'>Open menu</span>
                              <IconDotsVertical />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuItem
                              className='cursor-pointer'
                              onClick={() => {
                                setQuesionId(q?.question_id)
                              }}
                            >
                              View
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu> */}
                        <Button variant='ghost' className='p-0 text-[#0057B8] hover:text-[#0057B8]/80 hover:bg-transparent hover:underline' onClick={() => {
                          setQuesionId(q?.question_id)
                        }}>
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} className='h-24 text-center'>
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </AccordionContent>
      </AccordionItem>
      {!!quesntionId && (
        <QuestionDrawer
          fromChapterTable={true}
          questionId={quesntionId}
          setIsOpen={setQuesionId}
          typeValue={TypeValue.case_study}
        />
      )}
    </>
  )
})
